import { useMemo } from 'react';
import { extractCallableType, getProductConfig, ProductTypeConfig } from './index';

interface ProductData {
  productCode?: string;
  productName?: string; // Nombre del producto para extraer callableType
  productCallType?: 'autocall' | 'callable' | 'not-callable';
  underlyings?: any[];
  coupons?: any[];
  barrierLevel?: number | null;
  strikeLevel?: number | null;
  productTypeName?: string;
}

interface UseProductConfigResult {
  config: ProductTypeConfig;
  sections: ProductTypeConfig['sections'];
  shouldShow: {
    atGlance: boolean;
    barrier: boolean;
    underlyings: boolean;
    coupons: boolean;
    earlyRedemption: boolean;
    performance: boolean;
    performanceShowUnderlyingsInChart: boolean;
    couponDetails: boolean;
  };
}

/**
 * Hook para obtener configuración del producto
 * 
 * Uso:
 *   const { shouldShow, sections } = useProductConfig(product);
 *   {shouldShow.barrier && <ProductBarrierInfo ... />}
 */
export const useProductConfig = (product: ProductData | null): UseProductConfigResult => {
  return useMemo(() => {
    // Extraer callableType del nombre del producto
    const callableTypeFromName = extractCallableType(product?.productName);

    const callableTypeFromType = product?.productTypeName
      ? extractCallableType(product.productTypeName)
      : undefined;

    const callableType = (callableTypeFromName 
      && callableTypeFromName !== 'Standard')
        ? callableTypeFromName
        : ((callableTypeFromType 
            && callableTypeFromType !== 'Standard')
          ? callableTypeFromType
          : callableTypeFromName);
    
    // Obtener configuración usando code y callableType
    const config = getProductConfig(product?.productCode, callableType);
    
    // Log para debugging
    console.log('🎨 TEMPLATE SELECTION:', {
      productName: product?.productName,
      productCode: product?.productCode,
      extractedCallableType: callableType,
      registryKey: callableType ? `${product?.productCode}-${callableType}` : product?.productCode,
      selectedTemplate: config.name,
      templateCode: config.code,
      isDefault: config.code === 'default'
    });
    
    const sections = config.sections;
    // const callType = product?.productCallType || 'not-callable';

    const shouldShow = {
      atGlance: sections.atGlance.enabled,

      barrier:
        sections.barrier.enabled &&
        ((product?.barrierLevel != null && product.barrierLevel > 0) ||
          (product?.strikeLevel != null && product.strikeLevel > 0)),

      underlyings:
        sections.underlyings.enabled &&
        (product?.underlyings?.length ?? 0) > 0,

      coupons:
        sections.coupons.enabled &&
        (product?.coupons?.length ?? 0) > 0,

      // La lógica de earlyRedemption ahora está completamente en la configuración
      // Standard tiene enabled: false, Autocall y Callable tienen enabled: true
      earlyRedemption: sections.earlyRedemption.enabled,
      // earlyRedemption:
      //   sections.earlyRedemption.enabled &&
      //   (callType === 'autocall' || callType === 'callable'),
      performance: sections.performance.enabled,
      performanceShowUnderlyingsInChart: sections.performance.props.showUnderlyingsInChart !== false,
      couponDetails: sections.couponDetails.enabled
    };

    return { config, sections, shouldShow };
  }, [product]);
};

export default useProductConfig;

